import { OrbitControls as ThreeOrbitControls } from '@react-three/drei'
import type { OrbitControlsProps as ThreeOrbitControlsProps } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import React, { useRef } from 'react'
import * as THREE from 'three'

import SpringAnimate from '../util/spring-animate'

interface FirstPersonDragControlsProps {
  initPosition: THREE.Vector3
  initTarget: THREE.Vector3
  updatePosition?: Array<number>
  getPosition?: (position: THREE.Vector3) => void
  getTarget?: (target: THREE.Vector3) => void
  enablePan?: boolean
}

function FirstPersonDragControls({
  initPosition,
  initTarget,
  updatePosition = [],
  getPosition,
  getTarget,
  enablePan = false,
}: FirstPersonDragControlsProps) {
  const orbitRef = useRef(null)

  const { camera } = useThree()

  const HUMAN_EYE_LEVEL = 1.6

  const TARGET_OFFSET = 0.01

  const getTargetPositionBasedonPosition = (position: THREE.Vector3) => {
    const direction = new THREE.Vector3()
    camera.getWorldDirection(direction)
    return new THREE.Vector3()
      .copy(position)
      .add(direction.multiplyScalar(TARGET_OFFSET))
  }

  const springAnimate = SpringAnimate({
    initPosition: [initPosition.x, HUMAN_EYE_LEVEL, initPosition.z],
    mass: 5,
    tension: 160,
    friction: 50,
    orbitRef: orbitRef?.current as ThreeOrbitControlsProps | null,
    customTargetInterpolation: getTargetPositionBasedonPosition,
  })

  const handleFpvChange = (position: [number, number, number]) => {
    if (getPosition) {
      getPosition(new THREE.Vector3(...position))
    }
    if (getTarget) {
      getTarget(
        getTargetPositionBasedonPosition(new THREE.Vector3(...position))
      )
    }
  }

  React.useEffect(() => {
    if (updatePosition.length > 0) {
      const [x, , z] = updatePosition
      springAnimate.setPosition([x, HUMAN_EYE_LEVEL, z])
      handleFpvChange([x, HUMAN_EYE_LEVEL, z])
    }
  }, [updatePosition])

  React.useEffect(() => {
    if (camera) {
      camera.position.set(initPosition.x, HUMAN_EYE_LEVEL, initPosition.z)
      handleFpvChange([initPosition.x, HUMAN_EYE_LEVEL, initPosition.z])
    }
  }, [])

  return (
    <ThreeOrbitControls
      target={initTarget}
      camera={camera}
      ref={orbitRef}
      rotateSpeed={-0.5}
      enableZoom={false}
      enablePan={enablePan}
      maxPolarAngle={Math.PI}
      minPolarAngle={0}
      makeDefault
      enableDamping
      dampingFactor={0.4}
      enabled
    />
  )
}

export default FirstPersonDragControls
